/**
 * @documental
 * @export Field
 * @name
 * Checkbox.Field
 */

import { FC, useCallback, useMemo, useEffect } from "react";
import { FieldValues, useController, useFormContext } from "react-hook-form";
import { FieldBaseProps } from "../FieldBaseProps";
import { Input } from "./Input";
import { useInputDisabled } from "../InputGroup/useInputDisabled";
import { useInputReadonly } from "../InputGroup/useInputReadonly";
import { SubmitGroup } from "../SubmitGroup";
import { ValidityGroup } from "../ValidityGroup";
import { NoPaddingFormComponents } from "../shared";
import { VALIDATION } from "../../constants/validation";

/**
 * The `Field` component properties.
 */

export interface FieldProps extends FieldBaseProps<boolean | null | "indeterminate">, NoPaddingFormComponents {
  /**
   * Indicates whether the checkbox must be checked.
   */
  readonly required?: boolean;
}

/**
 * A React Hook Form compatible Checkbox component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field: FC<FieldProps> = (props) => {
  const { name, defaultValue = null, required = false, validator } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const validateHandler = useCallback(
    (value: boolean | "indeterminate", formValues: FieldValues) => {
      if (required && value !== true) return VALIDATION.REQUIRED;

      return validator ? validator(value, formValues) : true;
    },
    [required, validator],
  );

  const rules = useMemo(
    () => ({
      validate: disabled || readOnly ? undefined : validateHandler,
    }),
    [validateHandler, disabled, readOnly],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input {...props} ref={ref} value={value} onChange={onChange} onFocusExit={onBlur} />
      </ValidityGroup>
    </SubmitGroup>
  );
};
